import { Editor } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import { Placeholder } from '@tiptap/extensions'
import { fromHtml, toHtml } from '../../lib/markdown.js'

const Fez = window.Fez;
if (!Fez) throw new Error('fez runtime not loaded: load fez (window.Fez) before component modules');

// --- ui-md-editor ---
let __fez_component_0;
{
const MODE_KEY = 'md-editor-mode'
const MARKS = ['bold', 'italic', 'strike', 'link', 'bulletList', 'orderedList', 'blockquote', 'code']
__fez_component_0 = class {
  PROPS = {
    name: String,
    label: String,
    value: String,
    placeholder: String,
    rows: { type: Number, default: 8 },
    hint: String,
    error: String,
  }

  init() {
    this.state.mode = Fez.localStorage.get(MODE_KEY, 'rich') === 'markdown' ? 'markdown' : 'rich'
    this.state.active = {}
  }

  onMount() {
    this.state.editor = new Editor({
      element: this.state.editorNode,
      extensions: [
        // Underline has no markdown, so it would be lost on send.
        StarterKit.configure({ underline: false, heading: { levels: [1, 2, 3] }, link: { openOnClick: false, autolink: true, defaultProtocol: 'https' } }),
        Placeholder.configure({ placeholder: this.props.placeholder || '' }),
      ],
      editorProps: { attributes: { class: 'md-doc', 'aria-label': this.props.label || this.props.placeholder || 'Message', 'aria-multiline': 'true', role: 'textbox' } },
      onUpdate: () => this.sync(),
      onTransaction: () => this.track(),
      onFocus: () => this.track(),
      onBlur: () => this.track(),
    })
    return () => this.state.editor.destroy()
  }

  // A new `value` from the parent replaces the text, as in ui-textarea (also the first value).
  onRefresh(props, changed) {
    if ('value' in changed) this.setValue(props.value || '')
  }

  value() {
    return this.state.hidden?.value ?? ''
  }

  setValue(md) {
    if (this.state.mode === 'markdown') {
      this.state.source.value = md
      this.state.hidden.value = md
      this.fit()
      return
    }
    // Markdown drops leading blank lines; keep one empty line on top to write in, above a
    // signature or a quote.
    const top = /^[ \t]*\n/.test(md) ? '<p></p>' : ''
    this.state.editor.commands.setContent(top + toHtml(md), { emitUpdate: false })
    this.sync()
  }

  // At the cursor, or at the end when the editor never had one.
  insert(md) {
    if (this.state.mode === 'markdown') {
      const area = this.state.source
      const at = area.selectionStart ?? area.value.length
      area.value = `${area.value.slice(0, at)}${md}${area.value.slice(area.selectionEnd ?? at)}`
      area.focus()
      area.setSelectionRange(at + md.length, at + md.length)
      this.sourceInput()
      return
    }
    this.state.editor.chain().focus().insertContent(toHtml(md)).run()
  }

  focused() {
    return this.state.mode === 'markdown' ? document.activeElement === this.state.source : this.state.editor.isFocused
  }

  focusStart() {
    if (this.state.mode === 'markdown') {
      this.state.source.focus({ preventScroll: true })
      this.state.source.setSelectionRange(0, 0)
    } else {
      this.state.editor.commands.focus('start', { scrollIntoView: false })
    }
  }

  focusEnd() {
    if (this.state.mode === 'markdown') {
      const end = this.state.source.value.length
      this.state.source.focus()
      this.state.source.setSelectionRange(end, end)
    } else {
      this.state.editor.commands.focus('end')
    }
  }

  sync() {
    this.state.hidden.value = fromHtml(this.state.editor.getHTML())
  }

  sourceInput() {
    this.state.hidden.value = this.state.source.value
    this.fit()
  }

  // The toolbar shows what applies at the cursor while the editor has it; written only when it
  // changed.
  track() {
    const editor = this.state.editor
    const active = Object.fromEntries(MARKS.map((mark) => [mark, editor.isFocused && editor.isActive(mark)]))
    if (MARKS.some((mark) => active[mark] !== this.state.active[mark])) this.state.active = active
  }

  toggleMode() {
    if (this.state.mode === 'rich') {
      this.state.source.value = this.value()
      this.state.mode = 'markdown'
      this.tick().then(() => {
        this.fit()
        this.state.source.focus()
      })
    } else {
      this.state.mode = 'rich'
      this.state.editor.commands.setContent(toHtml(this.state.source.value), { emitUpdate: false })
      this.sync()
      this.tick().then(() => this.state.editor.commands.focus('end'))
    }
    Fez.localStorage.set(MODE_KEY, this.state.mode)
  }

  // The source grows with its text up to the same height the formatted view stops at.
  fit() {
    const area = this.state.source
    if (!area || this.state.mode !== 'markdown') return
    area.style.height = 'auto'
    if (area.scrollHeight) area.style.height = `${area.scrollHeight + area.offsetHeight - area.clientHeight}px`
  }

  run(command) {
    const chain = this.state.editor.chain().focus()
    const commands = {
      bold: () => chain.toggleBold(),
      italic: () => chain.toggleItalic(),
      strike: () => chain.toggleStrike(),
      code: () => chain.toggleCode(),
      bulletList: () => chain.toggleBulletList(),
      orderedList: () => chain.toggleOrderedList(),
      blockquote: () => chain.toggleBlockquote(),
    }
    commands[command]?.().run()
  }

  // On a link it removes the link; elsewhere it asks for the address.
  async link() {
    const editor = this.state.editor
    if (editor.isActive('link')) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    const href = await window.Dialog.open('name-dialog', { label: 'Address', placeholder: 'https://example.com', confirm: 'Add link' }, { title: 'Add a link' })
    if (!href) return editor.commands.focus()
    const chain = editor.chain().focus()
    if (editor.state.selection.empty) chain.insertContent({ type: 'text', text: href, marks: [{ type: 'link', attrs: { href } }] }).run()
    else chain.extendMarkRange('link').setLink({ href }).run()
  }

  CSS = `:fez {
display:block;.md-field{display:grid;gap:6px}.md-label{color:var(--color-text);font-size:var(--text-sm);font-weight:var(--weight-semibold)}.md-box{border:1px solid var(--color-border-strong);border-radius:var(--radius-md);background:var(--color-bg-raised);transition:border-color var(--duration-fast) var(--ease),box-shadow var(--duration-fast) var(--ease);&:focus-within{border-color:var(--color-accent);box-shadow:0 0 0 3px var(--color-accent-soft)}}.invalid .md-box{border-color:var(--color-danger)}.md-toolbar{display:flex;align-items:center;gap:2px;padding:var(--space-1);border-bottom:1px solid var(--color-border)}.md-sep{width:1px;height:18px;margin:0 var(--space-1);background:var(--color-border)}.md-grow{flex:1}.md-tool{display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;padding:0;border:0;border-radius:var(--radius-sm);background:none;color:var(--color-text-muted);cursor:pointer;transition:background var(--duration-fast) var(--ease),color var(--duration-fast) var(--ease);&:hover{background:var(--color-bg-hover);color:var(--color-text)}&.active{background:var(--color-selected-bg);color:var(--color-selected-text)}&:focus-visible{outline:2px solid var(--color-focus);outline-offset:-2px}}.md-rich,.md-source{min-height:calc(var(--rows) * 1.5em + 2 * var(--space-3));max-height:50vh;overflow-y:auto}.md-box.markdown .md-rich,.md-box.rich .md-source{display:none}.md-source{display:block;width:100%;padding:var(--space-3);border:0;border-radius:0 0 var(--radius-md) var(--radius-md);background:none;color:var(--color-text);font-family:var(--font-mono);font-size:var(--text-sm);line-height:1.6;resize:none;&:focus{outline:0}}.md-doc{min-height:inherit;padding:var(--space-3);&:focus{outline:0}p.is-editor-empty:first-child::before{content:attr(data-placeholder);float:left;height:0;color:var(--color-text-subtle);pointer-events:none}}.md-hint,.md-error{color:var(--color-text-muted);font-size:var(--text-xs)}.md-error{color:var(--color-danger);font-weight:var(--weight-medium)}
}`

  HTML = `<div class="md-field" class:invalid={props.error}>
{#if props.label}
<span class="md-label">{props.label}</span>
{/if}
<div class="md-box {state.mode}" style="--rows: {props.rows}">
<div class="md-toolbar" onmousedown="event.target.closest('button') && event.preventDefault()">
{#if state.mode === 'rich'}
<button type="button" class="md-tool" class:active={state.active.bold} title="Bold" aria-pressed={state.active.bold} onclick="fez.run('bold')"><ui-icon name="text-b" size="18"></ui-icon></button>
<button type="button" class="md-tool" class:active={state.active.italic} title="Italic" aria-pressed={state.active.italic} onclick="fez.run('italic')"><ui-icon name="text-italic" size="18"></ui-icon></button>
<button type="button" class="md-tool" class:active={state.active.strike} title="Strikethrough" aria-pressed={state.active.strike} onclick="fez.run('strike')"><ui-icon name="text-strikethrough" size="18"></ui-icon></button>
<button type="button" class="md-tool" class:active={state.active.link} title={state.active.link ? 'Remove link' : 'Link'} aria-pressed={state.active.link} onclick="fez.link()"><ui-icon name="link-simple" size="18"></ui-icon></button>
<span class="md-sep"></span>
<button type="button" class="md-tool" class:active={state.active.bulletList} title="Bulleted list" aria-pressed={state.active.bulletList} onclick="fez.run('bulletList')"><ui-icon name="list-bullets" size="18"></ui-icon></button>
<button type="button" class="md-tool" class:active={state.active.orderedList} title="Numbered list" aria-pressed={state.active.orderedList} onclick="fez.run('orderedList')"><ui-icon name="list-numbers" size="18"></ui-icon></button>
<button type="button" class="md-tool" class:active={state.active.blockquote} title="Quote" aria-pressed={state.active.blockquote} onclick="fez.run('blockquote')"><ui-icon name="quotes" size="18"></ui-icon></button>
<button type="button" class="md-tool" class:active={state.active.code} title="Code" aria-pressed={state.active.code} onclick="fez.run('code')"><ui-icon name="code" size="18"></ui-icon></button>
{/if}
<span class="md-grow"></span>
<button type="button" class="md-tool" class:active={state.mode === 'markdown'} title={state.mode === 'markdown' ? 'Back to formatted text' : 'Edit as markdown'} aria-pressed={state.mode === 'markdown'} onclick="fez.toggleMode()"><ui-icon name="markdown-logo" size="20"></ui-icon></button>
</div>
<div fez:keep="md-views">
<div class="md-rich" fez:this="editorNode"></div>
<textarea class="md-source" fez:this="source" rows={props.rows} aria-label={props.label || 'Message'} spellcheck="true" oninput="fez.sourceInput()"></textarea>
<!-- Kept with the views: a re-render would drop the value a hidden input holds as an attribute. -->
<input type="hidden" fez:this="hidden" name={props.name}>
</div>
</div>
{#if props.error}
<span class="md-error" role="alert">{props.error}</span>
{:else if props.hint}
<span class="md-hint">{props.hint}</span>
{/if}
</div>`
};
}

Fez('ui-md-editor', __fez_component_0);

export default __fez_component_0;
